// Quality presets for WHIP publishing
export interface QualityPreset {
  width: number;
  height: number;
  frameRate: number;
  bitrate: number;
  keyFrameInterval: number;  // キーフレーム間隔（フレーム数）
}

export const QUALITY_PRESETS: Record<string, QualityPreset> = {
  '480p': {
    width: 640,
    height: 480,
    frameRate: 30,
    bitrate: 1500000,  // 1.5Mbps
    keyFrameInterval: 90  // 3秒間隔（30fps × 3秒）
  },
  '720p': {
    width: 1280,
    height: 720,
    frameRate: 30,
    bitrate: 3000000,  // 3Mbps
    keyFrameInterval: 90  // 3秒間隔（30fps × 3秒）
  },
  '1080p': {
    width: 1920,
    height: 1080,
    frameRate: 30,
    bitrate: 5000000,  // 5Mbps
    keyFrameInterval: 90  // 3秒間隔（30fps × 3秒）
  },
};

// Publishing status
export type PublishStatus = 'stopped' | 'starting' | 'publishing' | 'error';

// WebRTC connection state
export type ConnectionState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';

// WHIP connection state
export type WHIPConnectionState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';

// Stream info
export interface StreamInfo {
  resolution: string;
  connectionState: ConnectionState;
}

// WHIP statistics
export interface WHIPStats {
  bitrate: number;  // kbps
  fps: number;
  resolution?: string;
  packetsSent?: number;
  packetsLost?: number;
  packetLossRate?: number;  // Phase0-3: Packet loss rate (%)
  jitter?: number;  // Phase0-3: Jitter (ms)
  rtt?: number;  // ms
}

// Playback status (viewer side)
export type PlaybackStatus = 'stopped' | 'starting' | 'connecting' | 'playing' | 'error';

// Playback statistics (viewer side)
export interface PlaybackStats {
  bitrate: number;  // kbps
  fps: number;
  resolution?: string;
  bufferLength?: number;  // seconds (for HLS/FLV)
  droppedFrames?: number;
  rtt?: number;  // ms (for WebRTC)
  packetsLost?: number;  // for WebRTC
}

// Player configuration
export interface PlayerConfig {
  serverType: 'srs' | 'ome';
  streamKey: string;
  onStatusChange?: (status: PlaybackStatus) => void;
  onStatsUpdate?: (stats: PlaybackStats) => void;
  onError?: (error: Error) => void;
  onBufferingChange?: (isBuffering: boolean) => void;  // バッファリング状態変更通知
}

// Viewer screen state (修正指示その３)
export type ViewerScreenState = 'before_entry' | 'party' | 'peek' | 'two_shot';

// Streamer status (修正指示その３)
export type StreamerStatus = 'waiting' | 'party' | 'two_shot';

// Streamer profile (Task 8)
export interface StreamerProfile {
  user_id: number;
  username: string;
  display_name: string | null;
  age: number | null;
  bust_size: string | null;
  waist_size: string | null;
  hip_size: string | null;
  profile_image_url: string | null;
  bio: string | null;
  genre: string | null;
  height: number | null;
  birthday: string | null;
  blood_type: string | null;
  region: string | null;
  occupation: string | null;
  features: string | null;
  personality: string | null;
  hobbies: string | null;
  favorite_food: string | null;
  chat_options: string | null;
  created_at: string;
  updated_at: string;
}

export interface StreamerProfileUpdate {
  display_name?: string;
  age?: number;
  bust_size?: string;
  waist_size?: string;
  hip_size?: string;
  profile_image_url?: string;
  bio?: string;
  genre?: string;
  height?: number;
  birthday?: string;
  blood_type?: string;
  region?: string;
  occupation?: string;
  features?: string;
  personality?: string;
  hobbies?: string;
  favorite_food?: string;
  chat_options?: string;
}

// Gallery image (Task 8)
export interface GalleryImage {
  id: number;
  streamer_id: number;
  image_url: string;
  upload_date: string;
  display_order: number;
}

export interface GalleryListResponse {
  images: GalleryImage[];
}

export interface GalleryImageCreate {
  image_url: string;
  display_order: number;
}

// Streamer message (Task 8)
export interface StreamerMessage {
  id: number;
  user_id: number;
  streamer_id: number;
  message: string;
  sent_at: string;
  is_read: boolean;
}

export interface StreamerMessageCreate {
  streamer_username: string;
  message: string;
}

export interface MessageSendResponse {
  message: string;
  remaining_points: number;
}

// Viewer memo (Task 8)
export interface ViewerMemo {
  id: number;
  user_id: number;
  streamer_id: number;
  memo: string;
  updated_at: string;
}

export interface ViewerMemoUpdate {
  memo: string;
}

export interface PartyCountResponse {
  party_count: number;
  peek_count: number;
}

// ==================== Moderation (content-moderation-controls) ====================

export type ModerationReasonCategory =
  | 'prohibited_content'
  | 'suspected_minor'
  | 'non_consensual'
  | 'rights_violation'
  | 'terms_violation'
  | 'other';

export interface ModerationReasonRequest {
  reason_category: ModerationReasonCategory;
  reason: string;
}

export type OmeDisconnectOutcome = 'disconnected' | 'not_found' | 'not_configured' | 'failed';

export interface OmeDisconnectResult {
  outcome: OmeDisconnectOutcome;
  detail: string | null;
}

export interface LiveStreamSummary {
  stream_key: string;
  schedule_id: string | null;
  streamer_id: number;
  streamer_username: string;
  streamer_display_name: string | null;
  broadcast_status: 'live' | 'paused' | 'suspended';
  started_at: string;
  party_count: number;
  peek_count: number;
}

export interface ForceStopResponse {
  stream_key: string;
  streamer_id: number;
  streamer_username: string;
  terminated_stream_keys: string[];
  ome: OmeDisconnectResult[];
  action_id: number;
}

export interface StreamerModerationState {
  streamer_id: number;
  streamer_username: string;
  streamer_display_name: string | null;
  affiliation_id: number | null;
  publish_blocked_at: string | null;
  publish_block_reason: string | null;
  suspended_at: string | null;
  suspension_reason: string | null;
}

export interface SuspensionResponse {
  state: StreamerModerationState;
  revoked_sessions: number;
  terminated_stream_keys: string[];
  ome: OmeDisconnectResult[];
}

export type ImageSubmissionStatus = 'pending' | 'approved' | 'rejected' | 'superseded';

export interface ImageSubmission {
  submission_id: number;
  status: ImageSubmissionStatus;
  submitted_at: string;
  reviewed_at: string | null;
}

export interface GalleryStatusItem {
  image_id: number;
  display_order: number;
  status: 'pending' | 'approved';
}

export interface MyModerationStatus {
  publish_blocked: boolean;
  suspended: boolean;
  profile_image_submission: ImageSubmission | null;
  gallery: GalleryStatusItem[];
}

export type NgWordAction = 'block' | 'review';

export interface NgWord {
  id: number;
  word: string;
  action: NgWordAction;
  source: 'sheet1' | 'custom';
  created_at: string;
  updated_at: string;
}

export type NgSurface =
  | 'profile'
  | 'notification'
  | 'chat'
  | 'secret_message'
  | 'streamer_message'
  | 'handle_name'
  | 'signup_name';

export interface NgDetection {
  id: number;
  surface: NgSurface;
  outcome: 'rejected' | 'flagged';
  matched_words: string[];
  author_kind: 'viewer' | 'staff';
  author_id: number | null;
  author_name: string | null;
  stream_key: string | null;
  target_streamer_id: number | null;
  target_streamer_username: string | null;
  ref_id: string | null;
  content_excerpt: string;
  created_at: string;
}

export interface NgDetectionListResponse {
  items: NgDetection[];
  total: number;
}

export type MediaKind = 'profile_image' | 'gallery_image';

export interface MediaReviewItem {
  kind: MediaKind;
  item_id: number;
  streamer_id: number;
  streamer_username: string;
  streamer_display_name: string | null;
  submitted_at: string;
  status: string;
  preview_path: string;
}

export interface StreamerGalleryEntry {
  id: number;
  image_url: string;
  display_order: number;
  status: 'pending' | 'approved';
  upload_date: string | null;
}

export interface StreamerContent {
  state: StreamerModerationState;
  profile_image_url: string | null;
  fields: Record<string, string | null>;
  gallery: StreamerGalleryEntry[];
  pending_profile_image: ImageSubmission | null;
}

export type ModerationActionType =
  | 'stream_force_stop'
  | 'publish_block_lift'
  | 'account_suspend'
  | 'account_suspension_lift'
  | 'media_approve'
  | 'media_reject'
  | 'gallery_takedown'
  | 'profile_image_takedown'
  | 'profile_field_takedown'
  | 'chat_message_takedown'
  | 'ng_word_create'
  | 'ng_word_update'
  | 'ng_word_delete';

export interface ModerationAction {
  id: number;
  action_type: ModerationActionType;
  actor_id: number | null;
  actor_username: string;
  actor_role: string;
  target_type: string;
  target_ref: string;
  target_streamer_id: number | null;
  target_streamer_username: string | null;
  target_url: string | null;
  reason_category: ModerationReasonCategory | null;
  reason: string;
  content_excerpt: string | null;
  result: 'succeeded' | 'partial';
  result_detail: string | null;
  created_at: string;
}

export interface ModerationActionListResponse {
  items: ModerationAction[];
  total: number;
}

export type MonthlyReportKind = 'actions' | 'complaints';
